import {
  Component,
  Input,
  signal,
  inject,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { OutboundCall } from '@modules/calls/Domain/call.model';
import { CallAudioService } from '@core/services/calls/call-audio.service';
import { OutboundCallService } from '@modules/calls/Application/outboundCall.service';
import { ToastService } from '@services/ui';
import { Subject, takeUntil } from 'rxjs';
import WaveSurfer from 'wavesurfer.js';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Button } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { NgIf } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from '@shared/shared.module';

@Component({
  selector: 'app-outbound-call-audio-dialog',
  templateUrl: './outbound-call-audio-dialog.component.html',
  styleUrls: ['./outbound-call-audio-dialog.component.scss'],
  standalone: true,
  imports: [
    DialogModule,
    ProgressSpinnerModule,
    NgIf,
    Button,
    DividerModule,
    SkeletonModule,
    TranslateModule,
    SharedModule,
  ],
})
export class OutboundCallAudioDialogComponent implements OnDestroy, OnChanges {
  @Input() visible: boolean = false;
  @Input() call: OutboundCall | null = null;
  @Input() outboundCallId: string | null = null;

  @ViewChild('waveformContainer', { static: false }) waveformContainer!: ElementRef;
  @ViewChild('timelineContainer', { static: false }) timelineContainer!: ElementRef;

  private callAudioService = inject(CallAudioService);
  private outboundCallService = inject(OutboundCallService);
  private toast = inject(ToastService);
  private destroy$ = new Subject<void>();

  isWaveformLoading = signal(false);
  selectedAudioUrl = signal<string | null>(null);
  playbackRate = 1;
  waveSurfer: WaveSurfer | null = null;

  get structuredSummary(): string[] {
    if (!this.call?.summary) return [];
    return this.call.summary
      .split('-')
      .map((item) => item.trim())
      .filter((item) => item);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue && this.visible) {
      if (this.call) {
        this.loadAudio();
      } else if (this.outboundCallId) {
        this.fetchCallDetails(this.outboundCallId);
      }
    }

    if (!this.visible && changes['visible']?.previousValue === true) {
      this.cleanupAudio();
    }
  }

  private fetchCallDetails(id: string): void {
    this.isWaveformLoading.set(true);
    this.outboundCallService
      .getOutboundCallById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (call: OutboundCall) => {
          this.call = call;
          this.loadAudio();
        },
        error: () => {
          this.toast.showError('Error', 'No se pudieron cargar los detalles de la llamada');
          this.isWaveformLoading.set(false);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupAudio();
  }

  private loadAudio(): void {
    if (!this.call?.id) return;

    this.isWaveformLoading.set(true);
    this.callAudioService.downloadAudio(this.call.id).subscribe({
      next: (blob) => {
        this.selectedAudioUrl.set(URL.createObjectURL(blob));

        setTimeout(() => {
          this.destroyWaveSurfer();

          if (this.waveformContainer?.nativeElement) {
            this.waveSurfer = WaveSurfer.create({
              container: this.waveformContainer.nativeElement,
              waveColor: '#5146e1',
              progressColor: '#92a1ad',
              cursorColor: '#11005b',
              height: 80,
              plugins: [
                TimelinePlugin.create({
                  container: this.timelineContainer.nativeElement,
                }),
              ],
            });

            this.waveSurfer.load(this.selectedAudioUrl()!);

            this.waveSurfer.setPlaybackRate(this.playbackRate);
          }

          this.isWaveformLoading.set(false);
        }, 0);
      },
      error: () => {
        this.toast.showError('Error', 'No se pudo descargar el audio');
        this.isWaveformLoading.set(false);
      },
    });
  }

  skipBackward(seconds: number = 5) {
    if (!this.waveSurfer) return;
    const current = this.waveSurfer.getCurrentTime();
    const duration = this.waveSurfer.getDuration();
    const newTime = Math.max(current - seconds, 0);
    this.waveSurfer.seekTo(newTime / duration);
  }

  skipForward(seconds: number = 5) {
    if (!this.waveSurfer) return;
    const current = this.waveSurfer.getCurrentTime();
    const duration = this.waveSurfer.getDuration();
    const newTime = Math.min(current + seconds, duration);
    this.waveSurfer.seekTo(newTime / duration);
  }

  setSpeed(rate: number) {
    this.playbackRate = rate;
    this.waveSurfer?.setPlaybackRate(rate);
  }

  togglePlayPause() {
    if (!this.waveSurfer) return;

    this.waveSurfer.playPause();
  }

  private cleanupAudio(): void {
    const currentUrl = this.selectedAudioUrl();
    if (currentUrl && currentUrl.trim() !== '') {
      URL.revokeObjectURL(currentUrl);
    }
    this.selectedAudioUrl.set(null);
    this.isWaveformLoading.set(false);

    this.destroyWaveSurfer();
  }

  private destroyWaveSurfer() {
    if (this.waveSurfer) {
      this.waveSurfer.destroy();
      this.waveSurfer = null;

      if (this.waveformContainer?.nativeElement) {
        this.waveformContainer.nativeElement.innerHTML = '';
      }
      if (this.timelineContainer?.nativeElement) {
        this.timelineContainer.nativeElement.innerHTML = '';
      }
    }
  }
}
